import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  TextInput,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { useResource } from '../lib/hooks';
import { formatDateTime, prettyJson } from '../lib/format';

interface AuditRow {
  id: string;
  actorType: string;
  actorId: string | null;
  actorLabel: string | null;
  action: string;
  targetType: string;
  targetId: string | null;
  targetLabel: string | null;
  before: unknown;
  after: unknown;
  metadata: unknown;
  ip: string | null;
  createdAt: string;
}

export function AuditPage() {
  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [selected, setSelected] = useState<AuditRow | null>(null);

  const query = new URLSearchParams({ pageSize: '100' });
  if (action) query.set('action', action);
  if (targetType) query.set('targetType', targetType);

  const { data, loading, reload } = useResource<{ items: AuditRow[]; total: number }>(
    `/api/admin/audit-logs?${query.toString()}`,
  );
  const actions = useResource<{ items: Array<{ action: string; count: number }> }>(
    '/api/admin/audit-logs/actions',
  );

  return (
    <>
      <PageHeader
        title="Dziennik audytu"
        description="Niezmienny zapis zmian konfiguracji i zdarzeń bezpieczeństwa. Wartości sekretów nigdy tu nie trafiają."
        actions={<Button onClick={reload}>Odśwież</Button>}
      />

      <Card tight>
        <div className="ah-card-body">
          <div className="ah-toolbar">
            <Field label="Akcja">
              {(id) => (
                <Select id={id} value={action} onChange={(event) => setAction(event.target.value)}>
                  <option value="">Wszystkie</option>
                  {(actions.data?.items ?? [])
                    .sort((a, b) => a.action.localeCompare(b.action))
                    .map((item) => (
                      <option key={item.action} value={item.action}>
                        {item.action} ({item.count})
                      </option>
                    ))}
                </Select>
              )}
            </Field>
            <Field label="Typ obiektu">
              {(id) => (
                <TextInput
                  id={id}
                  value={targetType}
                  placeholder="np. provider_account"
                  onChange={(event) => setTargetType(event.target.value)}
                />
              )}
            </Field>
          </div>
        </div>

        {loading ? (
          <div className="ah-card-body">
            <Skeleton rows={6} />
          </div>
        ) : (
          <DataTable
            rows={data?.items ?? []}
            rowKey={(row) => row.id}
            empty={<EmptyState title="Brak zdarzeń" />}
            columns={[
              { key: 'time', header: 'Czas', render: (row) => formatDateTime(row.createdAt) },
              {
                key: 'action',
                header: 'Akcja',
                render: (row) => <span className="ah-mono">{row.action}</span>,
              },
              {
                key: 'actor',
                header: 'Wykonawca',
                render: (row) => (
                  <>
                    <Badge tone={row.actorType === 'USER' ? 'info' : 'neutral'}>
                      {row.actorType}
                    </Badge>
                    <div style={{ fontSize: 12 }}>{row.actorLabel ?? '—'}</div>
                  </>
                ),
              },
              {
                key: 'target',
                header: 'Obiekt',
                render: (row) => (
                  <>
                    <div className="ah-mono" style={{ fontSize: 12 }}>
                      {row.targetType}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ah-text-muted)' }}>
                      {row.targetLabel ?? row.targetId ?? '—'}
                    </div>
                  </>
                ),
              },
              { key: 'ip', header: 'IP', render: (row) => row.ip ?? '—' },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) => (
                  <Button size="sm" onClick={() => setSelected(row)}>
                    Szczegóły
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Modal
        open={selected !== null}
        onOpenChange={(open) => !open && setSelected(null)}
        title={selected?.action ?? ''}
        description={`${selected?.targetType ?? ''} · ${formatDateTime(selected?.createdAt)}`}
      >
        {selected && (
          <div className="ah-grid cols-2">
            <div>
              <h3
                style={{ fontSize: 12, textTransform: 'uppercase', color: 'var(--ah-text-muted)' }}
              >
                Przed
              </h3>
              <pre className="ah-code">{prettyJson(selected.before)}</pre>
            </div>
            <div>
              <h3
                style={{ fontSize: 12, textTransform: 'uppercase', color: 'var(--ah-text-muted)' }}
              >
                Po
              </h3>
              <pre className="ah-code">{prettyJson(selected.after)}</pre>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <h3
                style={{ fontSize: 12, textTransform: 'uppercase', color: 'var(--ah-text-muted)' }}
              >
                Metadane
              </h3>
              <pre className="ah-code">{prettyJson(selected.metadata)}</pre>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
